import { useEffect, useState } from "react";
import { Button, Card, Form, Input, Modal, Radio, Table, Tag } from "antd";
import { DownloadOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  getInvoiceProfile,
  isValidTaxId,
  listInvoices,
  saveInvoiceProfile,
  type InvoiceProfile,
  type InvoiceRecord,
  type InvoiceStatus,
} from "@/lib/invoice/api";

const MUTED = "#999";

const formatMoney = (n: number) => `NT$ ${n.toLocaleString("en-US")}`;

const STATUS_COLOR: Record<InvoiceStatus, string> = {
  pending: "gold",
  issued: "green",
  void: "default",
};

function ProfileField({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex flex-col gap-1" style={{ minWidth: 0 }}>
      <span style={{ fontSize: 12, color: MUTED }}>{label}</span>
      <span style={{ fontSize: 14, wordBreak: "break-all" }}>{value || "—"}</span>
    </div>
  );
}

function InvoiceProfileCard() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<InvoiceProfile | null>(null);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm<InvoiceProfile>();
  const type = Form.useWatch("type", form);

  useEffect(() => {
    getInvoiceProfile().then(setProfile);
  }, []);

  const openEditor = () => {
    form.setFieldsValue(
      profile ?? { type: "b2b", title: "", taxId: "", email: "", address: "" }
    );
    setEditing(true);
  };

  const save = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    const next: InvoiceProfile = {
      type: values.type,
      title: values.type === "b2b" ? values.title.trim() : "",
      taxId: values.type === "b2b" ? values.taxId.trim() : "",
      email: values.email.trim(),
      address: values.address?.trim() ?? "",
    };
    await saveInvoiceProfile(next);
    setProfile(next);
    setEditing(false);
  };

  return (
    <Card size="small" data-testid="invoice-profile-card">
      <div className="flex items-center justify-between mb-3">
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          {t("employee.page.invoice.profileTitle")}
        </span>
        <Button size="middle" icon={<EditOutlined />} onClick={openEditor}>
          {t("employee.page.invoice.editProfile")}
        </Button>
      </div>
      {profile ? (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
          data-testid="invoice-profile"
        >
          <ProfileField
            label={t("employee.page.invoice.type")}
            value={t(`employee.page.invoice.types.${profile.type}`)}
          />
          {profile.type === "b2b" && (
            <>
              <ProfileField label={t("employee.page.invoice.title")} value={profile.title} />
              <ProfileField label={t("employee.page.invoice.taxId")} value={profile.taxId} />
            </>
          )}
          <ProfileField label={t("employee.page.invoice.email")} value={profile.email} />
          <ProfileField label={t("employee.page.invoice.address")} value={profile.address} />
        </div>
      ) : (
        <div style={{ fontSize: 13, color: MUTED }}>
          {t("employee.page.invoice.profileNotSet")}
        </div>
      )}
      <Modal
        open={editing}
        title={t("employee.page.invoice.profileTitle")}
        onCancel={() => setEditing(false)}
        onOk={save}
        okText={t("employee.page.confirm")}
        cancelText={t("employee.page.cancel")}
        forceRender
      >
        <Form form={form} layout="vertical" requiredMark>
          <Form.Item name="type" label={t("employee.page.invoice.type")}>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={[
                { value: "b2b", label: t("employee.page.invoice.types.b2b") },
                { value: "b2c", label: t("employee.page.invoice.types.b2c") },
              ]}
            />
          </Form.Item>
          {type === "b2b" && (
            <>
              <Form.Item
                name="title"
                label={t("employee.page.invoice.title")}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t("employee.page.invoice.titleRequired"),
                  },
                ]}
              >
                <Input data-testid="invoice-title-input" />
              </Form.Item>
              <Form.Item
                name="taxId"
                label={t("employee.page.invoice.taxId")}
                rules={[
                  {
                    validator: (_, v: string | undefined) =>
                      v && isValidTaxId(v.trim())
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("employee.page.invoice.taxIdInvalid"))),
                  },
                ]}
              >
                <Input maxLength={8} inputMode="numeric" data-testid="invoice-taxid-input" />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="email"
            label={t("employee.page.invoice.email")}
            rules={[
              { required: true, message: t("employee.page.invoice.emailRequired") },
              { type: "email", message: t("employee.page.invoice.emailInvalid") },
            ]}
          >
            <Input type="email" data-testid="invoice-email-input" />
          </Form.Item>
          <Form.Item name="address" label={t("employee.page.invoice.address")}>
            <Input data-testid="invoice-address-input" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

function InvoiceHistoryCard() {
  const { t } = useTranslation();
  const [records, setRecords] = useState<InvoiceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listInvoices()
      .then(setRecords)
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card size="small" data-testid="invoice-history-card">
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>
        {t("employee.page.invoice.historyTitle")}
      </div>
      <Table<InvoiceRecord>
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={records}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        scroll={{ x: 720 }}
        locale={{ emptyText: t("employee.page.invoice.empty") }}
        columns={[
          {
            title: t("employee.page.invoice.number"),
            dataIndex: "number",
            render: (v: string | null) => v ?? "—",
          },
          {
            title: t("employee.page.invoice.createdAt"),
            dataIndex: "createdAt",
            render: (v: string) => dayjs(v).format("YYYY-MM-DD"),
          },
          {
            title: t("employee.page.invoice.item"),
            dataIndex: "sourceOrderId",
            render: () => t("employee.page.invoice.seatOrderItem"),
          },
          {
            title: t("employee.page.invoice.subtotal"),
            dataIndex: "subtotal",
            align: "right",
            render: formatMoney,
          },
          {
            title: t("employee.page.invoice.tax"),
            dataIndex: "tax",
            align: "right",
            render: formatMoney,
          },
          {
            title: t("employee.page.invoice.total"),
            dataIndex: "total",
            align: "right",
            render: (v: number) => <span style={{ fontWeight: 600 }}>{formatMoney(v)}</span>,
          },
          {
            title: t("employee.page.invoice.status"),
            dataIndex: "status",
            render: (v: InvoiceStatus) => (
              <Tag color={STATUS_COLOR[v]}>{t(`employee.page.invoice.statuses.${v}`)}</Tag>
            ),
          },
          {
            title: "",
            key: "actions",
            align: "center",
            render: (_, r) => (
              <Button
                size="middle"
                icon={<DownloadOutlined />}
                disabled={r.status !== "issued"}
                aria-label={t("employee.page.invoice.download")}
              />
            ),
          },
        ]}
      />
    </Card>
  );
}

function InvoiceTab() {
  return (
    <div className="flex flex-col gap-4">
      <InvoiceProfileCard />
      <InvoiceHistoryCard />
    </div>
  );
}

export default InvoiceTab;
